import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  Sparkles,
  ShieldCheck,
  Phone,
  MessageCircle,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { FinalCTA } from "@/components/site/FinalCTA";
import { CountUp } from "@/components/site/CountUp";
import { Reveal } from "@/components/site/Reveal";
import { BRAND } from "@/lib/brand";
import {
  SERVICE_DETAILS,
  CLEANING_PRICING,
  SERVICES_INDUSTRIES,
  WHY_CHOOSE,
  SERVICES_PROCESS,
} from "@/lib/servicesData";

const HERO_IMG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2400&q=85";

export default function Services() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 600], [1.05, 1.15]);

  useEffect(() => {
    document.title =
      "Services | Interior Fit-Out, MEP, Cleaning & Maintenance — Bloc & Brush Dubai";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* HERO */}
      <section
        data-testid="services-hero"
        className="relative min-h-[92vh] flex items-end overflow-hidden"
      >
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={HERO_IMG} alt="Luxury Dubai apartment interior" className="w-full h-[120%] object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1A33]/25 via-[#0B1A33]/25 to-[#0B1A33]/80" />

        <div className="relative container mx-auto px-6 lg:px-12 max-w-7xl pt-32 pb-20 lg:pb-28 text-white">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-xs uppercase tracking-[0.28em] text-white/70"
          >
            Our Services
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15 }}
            data-testid="services-hero-title"
            className="font-display mt-6 text-5xl md:text-6xl lg:text-[78px] font-extrabold leading-[0.98] tracking-tight max-w-5xl"
          >
            Complete Property Solutions
            <br />
            <span className="text-white/65">Under One Roof.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="mt-8 text-base lg:text-[18px] text-white/75 max-w-2xl leading-relaxed"
          >
            Bloc & Brush is Dubai's trusted turnkey partner for Interior Fit-Out,
            Renovation, Holiday Home Furnishing, MEP, Facility Maintenance,
            Authority Approvals and Professional Cleaning. From planning and
            approvals to execution, maintenance and cleaning — one experienced
            team, one point of contact.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.45 }}
            className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <button
              onClick={() => setQuoteOpen(true)}
              data-testid="services-hero-quote-btn"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#13294B] hover:bg-white/90 rounded-full px-7 py-4 text-[15px] font-medium transition"
            >
              Get Free Quote <ArrowRight size={16} />
            </button>
            <a
              href="#services-grid"
              data-testid="services-hero-explore-btn"
              className="inline-flex items-center justify-center gap-2 border border-white/30 hover:bg-white/10 rounded-full px-7 py-4 text-[15px] font-medium transition"
            >
              Explore Our Services
            </a>
          </motion.div>

          <div className="mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-y-8 gap-x-6 border-t border-white/15 pt-10 max-w-5xl">
            {[
              { v: 100, suf: "+", label: "Projects Completed" },
              { label: "Residential & Commercial" },
              { label: "End-to-End Project Delivery" },
              { label: "Dubai-Wide Service Coverage" },
            ].map((s, i) => (
              <div key={i} className="flex items-start gap-3">
                <Check size={18} className="text-white/70 mt-1 shrink-0" />
                <div>
                  {s.v !== undefined ? (
                    <div className="font-display text-2xl lg:text-3xl font-extrabold">
                      <CountUp end={s.v} suffix={s.suf} />
                    </div>
                  ) : null}
                  <div className={`${s.v !== undefined ? "mt-1 text-xs uppercase tracking-[0.18em] text-white/55" : "text-[14px] text-white/80"}`}>
                    {s.label}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID + DETAILS */}
      <section
        id="services-grid"
        className="py-24 lg:py-32 bg-[#F5F5F5]"
        data-testid="services-grid"
      >
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">
              Services
            </span>
            <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
              Seven Disciplines.
              <br />
              <span className="text-[#13294B]">One Trusted Team.</span>
            </h2>
          </Reveal>

          <div className="mt-16 space-y-24 lg:space-y-32">
            {SERVICE_DETAILS.map((s, idx) => {
              const flipped = idx % 2 === 1;
              return (
                <article
                  key={s.slug}
                  id={s.slug}
                  data-testid={`service-detail-${s.slug}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
                >
                  <Reveal
                    className={`lg:col-span-6 ${flipped ? "lg:order-2" : ""}`}
                  >
                    <div className="relative">
                      <div className="aspect-[4/5] overflow-hidden rounded-3xl">
                        <img
                          src={s.image}
                          alt={s.title}
                          loading="lazy"
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-[1.4s] ease-out"
                        />
                      </div>
                      <div className="absolute top-6 left-6 bg-white/90 backdrop-blur px-4 py-2 rounded-full text-xs font-semibold text-[#13294B] tracking-[0.18em]">
                        {s.eyebrow} · {s.title.toUpperCase()}
                      </div>
                    </div>
                  </Reveal>

                  <Reveal
                    delay={0.1}
                    className={`lg:col-span-6 ${flipped ? "lg:order-1" : ""}`}
                  >
                    <h3 className="font-display text-3xl lg:text-[44px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                      {s.title}
                    </h3>
                    <p className="mt-5 text-[17px] leading-[1.8] text-[#1C1C1C]/70">
                      {s.intro}
                    </p>

                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
                      {s.groups.map((g) => (
                        <div key={g.h}>
                          <h4 className="font-display text-sm uppercase tracking-[0.18em] text-[#13294B] font-bold">
                            {g.h}
                          </h4>
                          <ul className="mt-3 space-y-1.5">
                            {g.items.map((item) => (
                              <li
                                key={item}
                                className="text-[14px] text-[#1C1C1C]/70 flex items-start gap-2"
                              >
                                <span className="w-1 h-1 rounded-full bg-[#13294B] mt-2 shrink-0" />
                                {item}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>

                    {/* Cleaning pricing tables */}
                    {s.slug === "professional-cleaning" && (
                      <div
                        data-testid="cleaning-pricing"
                        className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5"
                      >
                        {CLEANING_PRICING.map((p) => (
                          <div
                            key={p.title}
                            className="bg-white rounded-2xl p-6 border border-black/5 hover:border-[#13294B]/20 hover:shadow-[0_18px_40px_rgba(19,41,75,0.08)] transition-all duration-500"
                          >
                            <div className="flex items-center justify-between">
                              <h5 className="font-display font-bold text-[#1C1C1C] text-[15px]">
                                {p.title}
                              </h5>
                              <span className="text-[10px] uppercase tracking-[0.18em] text-[#707070]">
                                Fixed Price
                              </span>
                            </div>
                            <ul className="mt-4 divide-y divide-black/5">
                              {p.rows.map(([label, price]) => (
                                <li
                                  key={label}
                                  className="flex items-center justify-between py-2.5 text-[14px]"
                                >
                                  <span className="text-[#1C1C1C]/70">{label}</span>
                                  <span className="font-semibold text-[#13294B]">
                                    {price}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="mt-10 flex flex-wrap items-center gap-4">
                      <button
                        onClick={() => setQuoteOpen(true)}
                        data-testid={`service-cta-${s.slug}`}
                        className="inline-flex items-center gap-2 bg-[#13294B] hover:bg-[#0E1F3A] text-white rounded-full px-7 py-3.5 text-[15px] font-medium transition"
                      >
                        {s.cta} <ArrowRight size={16} />
                      </button>
                      <a
                        href={BRAND.whatsappHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[15px] font-medium text-[#13294B] hover:underline underline-offset-4"
                      >
                        Chat on WhatsApp <ArrowUpRight size={14} />
                      </a>
                    </div>
                  </Reveal>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="py-24 lg:py-32 bg-white" data-testid="services-why">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">
                Why Bloc & Brush
              </span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                Why Dubai's Most Discerning
                <br />
                <span className="text-[#13294B]">Owners Choose Us.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-5">
            {WHY_CHOOSE.map((w, i) => (
              <Reveal key={w} delay={(i % 4) * 0.05}>
                <div className="group h-full bg-[#F5F5F5] hover:bg-[#13294B] rounded-2xl p-7 transition-colors duration-500 cursor-default">
                  <ShieldCheck
                    size={22}
                    strokeWidth={1.6}
                    className="text-[#13294B] group-hover:text-white transition-colors"
                  />
                  <div className="font-display mt-7 text-[17px] font-bold text-[#1C1C1C] group-hover:text-white transition-colors">
                    {w}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS TIMELINE */}
      <section
        className="py-24 lg:py-32 bg-[#0B1A33] text-white relative overflow-hidden"
        data-testid="services-process"
      >
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-white/55">
                Our Process
              </span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight">
                From First Call
                <br />
                <span className="text-white/65">To Long-Term Support.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-16 relative">
            <div className="hidden lg:block absolute top-[14px] left-0 right-0 h-px bg-white/15" />
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-y-10 gap-x-5">
              {SERVICES_PROCESS.map((step, i) => (
                <Reveal key={step} delay={i * 0.05}>
                  <div>
                    <div className="w-7 h-7 rounded-full bg-white text-[#13294B] text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </div>
                    <h3 className="font-display mt-5 text-[15px] font-bold tracking-tight">
                      {step}
                    </h3>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 lg:py-32 bg-white" data-testid="services-industries">
        <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
          <Reveal>
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.22em] text-[#707070]">
                Industries
              </span>
              <h2 className="font-display mt-4 text-4xl lg:text-[48px] font-extrabold leading-[1.05] tracking-tight text-[#1C1C1C]">
                Industries We Serve
                <br />
                <span className="text-[#13294B]">Across Dubai.</span>
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {SERVICES_INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.label} delay={(i % 4) * 0.05}>
                <div className="group relative overflow-hidden rounded-2xl aspect-[4/5] bg-[#0B1A33]">
                  <img
                    src={ind.img}
                    alt={ind.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover opacity-75 group-hover:opacity-95 group-hover:scale-[1.06] transition-all duration-[1.2s] ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A33] via-[#0B1A33]/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">
                      Industry
                    </div>
                    <div className="font-display mt-1 text-[16px] font-semibold">
                      {ind.label}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA onQuoteClick={() => setQuoteOpen(true)} />
      <Footer onQuoteClick={() => setQuoteOpen(true)} />
      <WhatsAppFloat />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
    </div>
  );
}
